import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { Alert, toastStore } from 'shared/ui'
import { type IAlert, type IAlertAction } from 'shared/ui/Alert/types'
import { uiStore } from 'shared/store/uiStore'
import { type WorkflowExecution } from 'entities/Workflow'
import { type Contact } from 'entities/Contacts/model/Contact'
import { type Conversation } from 'entities/Conversation/model/Conversation'
import { subscribeConversationStateChange } from 'entities/Conversation/events/EventConversationStateChanged'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { type IConversationState, type IParamsConversationState } from 'entities/Conversation'
import { useEventActivityCreated } from 'entities/Activity/events/EventActivityCreated'
import {
  ChatbotExecutionProvider,
  useChatbotExecutionContext,
} from 'widgets/ConversationMessages/ui/ConversationMessageFieldAlerts/ConversationChatbotAlert/context/ChatbotExecutionContext'
import type { ChatbotExecutionStore } from 'widgets/ConversationMessages/ui/ConversationMessageFieldAlerts/ConversationChatbotAlert/store/ChatbotExecutionStore'

type IConversationChatbotAlertProps = {
  contact?: Contact
  execution: WorkflowExecution
  changeConversationState?: (params: IParamsConversationState, execution: WorkflowExecution) => void
}

type IConversationChatbotAlertConfig = {
  type: IAlert['type']
  title: string
  desc: string
  showPauseButton: boolean
  pauseButtonText: string
  pauseAction: IConversationState
}

const ConversationChatbotAlert = observer(
  ({ contact, execution, changeConversationState }: IConversationChatbotAlertProps) => {
    const contactName = contact?.full_name || (contact?.formatted_number ?? 'user')
    const workflowName = execution.workflowName

    const getTimeToResume = () => {
      if (!execution.pausedAt) return '1 hour'

      const pauseTime = uiStore.dayjs(execution.pausedAt)
      const resumeTime = pauseTime.add(execution.pauseOptions.timeout, 's')
      const now = uiStore.dayjs()
      const diffMinutes = Math.ceil(resumeTime.diff(now, 'minutes', true))

      if (diffMinutes <= 0) return '1 minute'
      if (diffMinutes < 60) return `${diffMinutes} minutes`

      const diffHours = Math.floor(diffMinutes / 60)
      const remainingMinutes = diffMinutes % 60

      if (remainingMinutes === 0) {
        return `${diffHours} hour${diffHours > 1 ? 's' : ''}`
      }

      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ${remainingMinutes} minutes`
    }

    const alertConfig: Record<string, IConversationChatbotAlertConfig> = {
      paused: {
        type: 'infoStarsLight',
        title: 'AI Agent paused',
        desc: `"${workflowName}" is paused. Auto-resumes in ${getTimeToResume()}.`,
        showPauseButton: true,
        pauseButtonText: 'Resume AI Agent',
        pauseAction: 'resume',
      },
      active: {
        type: 'infoStars',
        title: 'AI Agent engaged',
        desc: `"${workflowName}" is currently chatting with ${contactName}`,
        showPauseButton: true,
        pauseButtonText: 'Pause AI Agent',
        pauseAction: 'pause',
      },
      error: {
        type: 'error',
        title: '',
        desc: 'Something went wrong. Please claim conversation to continue.',
        showPauseButton: false,
        pauseButtonText: '',
        pauseAction: '',
      },
    }

    const currentStatus = execution.currentStatus
    const config = alertConfig[currentStatus]
    const params: IParamsConversationState = {
      state: config.pauseAction,
      initiator_id: execution.id,
      initiator_type: 'workflow_execution',
    }

    const handleClick = (state: IConversationState) => {
      execution.setLoading(true)
      changeConversationState?.({ ...params, state }, execution)
    }

    const pauseAction: IAlertAction | null = config.showPauseButton
      ? {
          variant: 'button',
          type: 'primary',
          text: config.pauseButtonText,
          onClick: () => {
            execution.setLoading(true)
            changeConversationState?.(params, execution)
          },
          disabled: execution.loading,
        }
      : null

    const claimAction: IAlertAction | null = !execution.isPauseSupported
      ? {
          variant: 'button',
          type: 'primary',
          text: 'Claim conversation',
          onClick: () => handleClick('claim'),
          disabled: execution.loading,
        }
      : null

    return (
      <Alert
        item={{
          type: config.type,
          title: config.title,
          desc: config.desc,
        }}
        actions={
          execution.isPauseSupported
            ? [
                ...(pauseAction ? [pauseAction] : []),
                {
                  variant: 'button',
                  type: execution ? 'secondary' : 'primary',
                  text: 'Stop & Claim',
                  onClick: () => handleClick('claim'),
                  disabled: execution.loading,
                },
              ]
            : undefined
        }
        rightActions={[...(claimAction ? [claimAction] : [])]}
      />
    )
  }
)

type IConversationChatbotAlertsBodyProps = {
  conversation: Conversation
}

const ConversationChatbotAlertsBody = observer(
  ({ conversation }: IConversationChatbotAlertsBodyProps) => {
    const { executions, dispose, syncLatestExecutions, changeConversationState } =
      useChatbotExecutionContext()
    const { websocket } = useEntitiesContext()
    const conversationId = conversation.id
    const conversationContact = conversation.contact

    useEffect(
      () =>
        subscribeConversationStateChange(websocket, ({ id }) => {
          if (id === conversationId) {
            syncLatestExecutions(conversationId)
          }
        }),
      [conversation]
    )

    useEventActivityCreated((data) => {
      if (data.activity_log.conversation_id === conversationId) {
        const type = data.activity_log.type

        if (type === 'chatbot_paused') {
          toastStore.add({
            type: 'info',
            title: 'AI Agent paused',
            desc: 'You can now send messages manually',
          })
        }
        if (type === 'chatbot_finish_success') {
          toastStore.add({
            type: 'success',
            title: 'Goal completed by AI Agent',
            desc: 'The conversation was successfully handled. You can jump in anytime if you’d like to follow up.',
          })
        }
        if (type === 'chatbot_finish_fallback') {
          toastStore.add({
            type: 'info',
            title: 'AI Agent ended the conversation - goal not met',
            desc: 'The AI Agent couldn’t complete the goal. You can take over now if further action is needed.',
          })
        }
        if (type === 'chatbot_claimed' || type === 'chatbot_finish_stop_disabled') {
          toastStore.add({
            type: 'info',
            title: 'AI Agent stopped',
            desc: 'You’ve claimed the conversation and can now send messages manually',
          })
        }
      }
    })

    useEffect(() => {
      return () => {
        dispose()
      }
    }, [])

    useEffect(() => {
      syncLatestExecutions(conversationId)

      return () => {
        dispose()
      }
    }, [conversationId])

    return (
      <>
        {executions.map((execution) => (
          <ConversationChatbotAlert
            key={execution.id}
            execution={execution}
            contact={conversationContact}
            changeConversationState={(params, execution) =>
              changeConversationState(conversation.id, execution, params)
            }
          />
        ))}
      </>
    )
  }
)

type IConversationChatbotAlertsProps = {
  store: ChatbotExecutionStore
  conversation: Conversation
}

export const ConversationChatbotAlerts = ({
  store,
  conversation,
}: IConversationChatbotAlertsProps) => {
  if (conversation.id === null) return null

  return (
    <ChatbotExecutionProvider store={store}>
      <ConversationChatbotAlertsBody conversation={conversation} />
    </ChatbotExecutionProvider>
  )
}
